'use client';

import { useState, useCallback, useEffect } from 'react';

export interface ViewedHotel {
  slug: string;            // Page_Url, used to dedup and to build the URL
  name: string;
  city: string;
  country: string;
  starRating: number;
  primaryImage?: string;
  url: string;             // canonical detail URL incl. checkin/checkout/etc — used as-is for navigation (with past-date bump applied at render time)
  viewedAt: number;
}

const KEY = 'bip_hotel_recently_viewed';
const MAX = 4; // WS1 / BCP-139: CMS caps recently-viewed at 4

// Schema upgraded from {id, name, city, starRating, primaryImage, viewedAt}
// to {slug, name, city, country, starRating, primaryImage, url, viewedAt}.
// Drop any pre-existing entries that lack the new required fields rather than
// render them with undefined `slug`/`url` (would yield React key collisions
// + dead links). Cheap one-time migration on read.
function isValidEntry(e: unknown): e is ViewedHotel {
  if (!e || typeof e !== 'object') return false;
  const v = e as Record<string, unknown>;
  return typeof v.slug === 'string' && typeof v.url === 'string' && typeof v.name === 'string';
}

function readStorage(): ViewedHotel[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(isValidEntry) : [];
  } catch {
    return [];
  }
}

function writeStorage(items: ViewedHotel[]) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    // Quota — drop the oldest half and retry once before giving up.
    try {
      localStorage.setItem(KEY, JSON.stringify(items.slice(0, Math.ceil(items.length / 2))));
    } catch {
      try { localStorage.removeItem(KEY); } catch { /* ignore */ }
    }
  }
}

export function useRecentlyViewed() {
  const [viewed, setViewed] = useState<ViewedHotel[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setViewed(readStorage());
    setHydrated(true);
  }, []);

  const track = useCallback((hotel: Omit<ViewedHotel, 'viewedAt'>) => {
    setViewed((prev) => {
      const deduped = prev.filter((h) => h.slug !== hotel.slug);
      const next = [{ ...hotel, viewedAt: Date.now() }, ...deduped].slice(0, MAX);
      writeStorage(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setViewed([]);
    try { localStorage.removeItem(KEY); } catch { /* ignore */ }
  }, []);

  return { viewed, track, clear, hydrated };
}
